import React, { useEffect, useMemo, useRef, useState } from 'react'
import { motion } from 'motion/react'
import audioService from '../../../services/audioService'

const TOTAL_ITEMS = 80
const WIN_INDEX = 60
const ITEM_SIZE = 56
const ROULETTE_DURATION = 7000
const ROULETTE_CENTER_PAUSE = 1000
const ROULETTE_CENTER_DURATION = 1200

// Формируем стабильный идентификатор игрока
const getUserId = (user, index) => String(user?.chat_id || user?.id || user?.name || `user-${index}`)

// Нормализация весов для пропорционального распределения
const normalizeWeights = (users) => {
  const weights = users.map((user) => {
    const percent = Number(user?.percent || 0)
    const total = Number(user?.total_crystal || user?.total || 0)
    return percent > 0 ? percent : total > 0 ? total : 1
  })
  const sum = weights.reduce((acc, value) => acc + value, 0) || 1
  return weights.map((value) => value / sum)
}

// Собираем список игроков с повторениями по их шансам
const buildWeightedList = (users, minRepeats = 2) => {
  if (!users.length) return []
  const weights = normalizeWeights(users)
  const baseCount = Math.max(minRepeats, 1)
  const minTotal = baseCount * users.length
  const targetTotal = Math.max(TOTAL_ITEMS, minTotal)
  let remaining = targetTotal - minTotal

  const counts = users.map((_, index) => {
    const extra = remaining > 0 ? Math.round(remaining * weights[index]) : 0
    return baseCount + extra
  })

  let diff = targetTotal - counts.reduce((acc, value) => acc + value, 0)
  let cursor = 0
  while (diff !== 0 && users.length > 0) {
    counts[cursor % users.length] += diff > 0 ? 1 : -1
    diff += diff > 0 ? -1 : 1
    cursor += 1
  }

  const list = []
  users.forEach((user, index) => {
    for (let i = 0; i < counts[index]; i += 1) {
      list.push(user)
    }
  })

  // Лёгкая рандомизация итогового списка
  for (let i = list.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[list[i], list[j]] = [list[j], list[i]]
  }

  return list
}

// Находим победителя по данным бэкенда
const pickWinnerUser = (users, winner) => {
  if (!winner) return users[0]
  const winnerId = String(winner.chat_id || winner.id || winner.name || '')
  return users.find((user) => String(user.chat_id || user.id || user.name) === winnerId) || winner
}

const RoyaleRoulette = ({ users = [], winner, startKey = 0, onFinish }) => {
  const [items, setItems] = useState([])
  const [winnerKey, setWinnerKey] = useState(null)
  const trackRef = useRef(null)
  const timeoutRef = useRef(null)
  const startAttemptRef = useRef(0)
  const latestUsersRef = useRef(users)
  const latestWinnerRef = useRef(winner)

  useEffect(() => {
    // Храним актуальные данные, но не перезапускаем рулетку
    latestUsersRef.current = users
    latestWinnerRef.current = winner
  }, [users, winner])

  useEffect(() => {
    if (!startKey) return

    const snapshotUsers = latestUsersRef.current || []
    const snapshotWinner = latestWinnerRef.current
    const winnerUser = pickWinnerUser(snapshotUsers, snapshotWinner)

    if (!winnerUser || snapshotUsers.length === 0) return

    // Генерируем ленту рулетки и фиксируем победителя
    const baseList = buildWeightedList(snapshotUsers)
    const prepared = [...baseList]
    while (prepared.length <= WIN_INDEX) {
      prepared.push(snapshotUsers[Math.floor(Math.random() * snapshotUsers.length)])
    }
    prepared[WIN_INDEX] = winnerUser
    // Запоминаем победителя, чтобы подсветить его в ленте
    setWinnerKey(getUserId(winnerUser, 0))

    setItems(prepared)

    const track = trackRef.current
    if (track) {
      track.style.transition = 'none'
      track.style.transform = 'translateX(0)'
    }

    if (timeoutRef.current) clearTimeout(timeoutRef.current)

    audioService.play('royaleRouletteSpin')
    // Вибрация при старте рулетки
    try {
      const haptic = window.Telegram?.WebApp?.HapticFeedback
      if (haptic?.impactOccurred) {
        haptic.impactOccurred('medium')
      }
    } catch (error) {
      // Игнорируем ошибки вибрации
    }

    const startAnimation = () => {
      const target = trackRef.current
      if (!target) return false

      const container = target.parentElement
      if (!container) return false

      const targetItem = target.children?.[WIN_INDEX]
      if (!targetItem) return false

      // Центруем реальный элемент с учетом gap/padding/рамок
      const containerCenter = container.offsetWidth / 2
      const itemWidth = targetItem.offsetWidth

      // Случайная точка остановки: начало/конец/рандом
      const stopModes = ['start', 'end', 'random']
      const stopMode = stopModes[Math.floor(Math.random() * stopModes.length)]
      const edgePadding = 5
      const minOffset = edgePadding
      const maxOffset = Math.max(edgePadding, itemWidth - edgePadding)
      const stopOffset =
        stopMode === 'start'
          ? minOffset
          : stopMode === 'end'
            ? maxOffset
            : minOffset + Math.random() * Math.max(0, maxOffset - minOffset)

      const targetPoint = targetItem.offsetLeft + stopOffset
      const finalPosition = targetPoint - containerCenter
      const centerPoint = targetItem.offsetLeft + itemWidth / 2
      const centerPosition = centerPoint - containerCenter

      target.style.transition = `transform ${ROULETTE_DURATION}ms cubic-bezier(.01,0,.3,1)`
      target.style.transform = `translateX(-${Math.max(0, finalPosition)}px)`

      timeoutRef.current = setTimeout(() => {
        if (!trackRef.current) return

        timeoutRef.current = setTimeout(() => {
          if (!trackRef.current) return
          // Доворачиваем до центра, чтобы подтвердить победителя
          trackRef.current.style.transition = `transform ${ROULETTE_CENTER_DURATION}ms cubic-bezier(.22,.99,.45,1)`
          trackRef.current.style.transform = `translateX(-${Math.max(0, centerPosition)}px)`

          timeoutRef.current = setTimeout(() => {
            if (onFinish) onFinish()
          }, ROULETTE_CENTER_DURATION)
        }, ROULETTE_CENTER_PAUSE)
      }, ROULETTE_DURATION)

      return true
    }

    const attemptStart = () => {
      startAttemptRef.current += 1
      if (startAnimation()) return
      if (startAttemptRef.current >= 6) return

      // Ждем следующий кадр/обновление DOM, если элементы еще не смонтированы
      timeoutRef.current = setTimeout(() => {
        requestAnimationFrame(attemptStart)
      }, 50)
    }

    startAttemptRef.current = 0
    requestAnimationFrame(attemptStart)

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [startKey, onFinish])

  if (!items.length) return null

  return (
    <motion.div
      className="rounded-2xl border border-white/10 bg-surface/60 py-3 lg:py-4"
      initial={{ opacity: 0, y: -12, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
    >
      <div className="relative overflow-hidden rounded-xl bg-dark-primary/70 py-2 lg:py-3">
        <div ref={trackRef} className="flex items-center gap-1 px-2 lg:gap-2 lg:px-3">
          {items.map((user, index) => (
            <div
              key={`${getUserId(user, index)}-${index}`}
              className={`w-14 h-14 min-w-[56px] min-h-[56px] shrink-0 rounded-full border overflow-hidden lg:w-16 lg:h-16 lg:min-w-[64px] lg:min-h-[64px] ${
                winnerKey && index === WIN_INDEX && getUserId(user, index) === winnerKey
                  ? 'border-white/30'
                  : 'border-white/30'
              }`}
            >
              <img
                src={user?.ava}
                alt={user?.name || 'User'}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>
        <div className="absolute inset-y-0 left-1/2 w-0.5 bg-accent-primary/70 shadow-[0_0_6px_rgba(0,255,127,0.6)]" />
      </div>
    </motion.div>
  )
}

export default RoyaleRoulette
